/**
 * SnapshotCacheUpdater - Utility for updating paragraph snapshot caches
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for managing paragraph snapshot caches,
 * which are used for incremental pagination and layout operations.
 */

import type { ParagraphSnapshot } from '../../../core/types/Typesetter';
import { WasmLogger } from '../../../../../utils/loggers/WasmLogger';

// Debug flag - set to true to enable multi-paragraph merge diagnostics
const DEBUG_ENABLED = true;

/**
 * Line type from ParagraphSnapshot (inferred)
 */
type SnapshotLine = NonNullable<ParagraphSnapshot['lines']>[number];

/**
 * Deduplicate lines by their grapheme range (start_grapheme_index, end_grapheme_index).
 * 
 * When two lines have the same start-end range, this is a bug in merge logic.
 * This function acts as a SAFETY NET to prevent DUPLICATE_LINE_RANGES errors at render time.
 * 
 * Strategy:
 * - Keep the FIRST occurrence of each range (assumes WASM lines come first after merge)
 * - Log diagnostics to help identify the root cause
 * 
 * @param lines - Array of lines (may contain duplicates)
 * @param source - String identifying where this dedup is called from (for logging)
 * @returns Deduplicated array of lines
 */
function deduplicateLinesByRange(
  lines: SnapshotLine[],
  source: string
): SnapshotLine[] {
  if (!lines || lines.length === 0) return lines;
  
  const seen = new Map<string, { line: SnapshotLine; index: number }>();
  const duplicates: Array<{ key: string; indices: number[]; lines: SnapshotLine[] }> = [];
  
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const key = `${line.start_grapheme_index}-${line.end_grapheme_index}`;
    
    if (seen.has(key)) {
      // Found a duplicate - track it for logging
      const existing = seen.get(key)!;
      let dupEntry = duplicates.find(d => d.key === key);
      if (!dupEntry) {
        dupEntry = { key, indices: [existing.index], lines: [existing.line] };
        duplicates.push(dupEntry);
      }
      dupEntry.indices.push(i);
      dupEntry.lines.push(line);
    } else {
      seen.set(key, { line, index: i });
    }
  }
  
  // Log if duplicates were found (helps track down root cause)
  if (duplicates.length > 0) {
    console.warn(`🔬 [DEDUP:${source}] Removed ${duplicates.length} duplicate line range(s):`, {
      duplicates: duplicates.map(d => ({
        range: d.key,
        occurrences: d.indices.length + 1,
        indices: [d.indices[0], ...d.indices.slice(1)],
        // Include Y positions to help diagnose stale vs fresh
        yPositions: d.lines.map(l => Math.round((l.baseline_y_fp || 0) / 64))
      }))
    });
    // Log stack trace to identify call path
    console.trace(`🔬 [DEDUP:${source}] Stack trace for duplicate detection`);
  }
  
  // Return only first occurrence of each range
  return Array.from(seen.values())
    .sort((a, b) => a.index - b.index) // Maintain original order
    .map(entry => entry.line);
}

/**
 * Get the stable paragraph key from a snapshot.
 * 
 * CRITICAL: Uses paragraph_id (stable across re-layouts) NOT snapshot_id (changes on every re-layout).
 * This ensures that when a paragraph is re-laid out, its new snapshot REPLACES the old one
 * instead of being added as a duplicate.
 * 
 * Falls back to snapshot_id if paragraph_id is not available.
 */
export function getParaKey(snapshot: ParagraphSnapshot): string {
  // Prefer paragraph_id (stable) over snapshot_id (changes on re-layout)
  return snapshot.paragraph_id || snapshot.snapshot_id;
}

/**
 * Edit delta information for adjusting grapheme indices during merge
 */
export interface EditDeltaInfo {
  position: number;      // Where the edit occurred (grapheme index)
  deleteCount: number;   // Number of graphemes deleted
  insertLength: number;  // Number of graphemes inserted
  committedLength?: number; // Optional: Total document length AFTER the edit (for stale line filtering)
}

/**
 * Calculate the new snapshot cache array from existing cache and new snapshots
 * 
 * ⚠️ DEPRECATED: This function does LINE-LEVEL merging which can pollute WASM's
 * complete output with stale cache data, causing DUPLICATE_LINE_RANGES errors.
 * 
 * PREFER: mergeSnapshotsByParagraphId() which does PARAGRAPH-LEVEL merging and
 * trusts WASM output completely for affected paragraphs.
 * 
 * WASM returns COMPLETE ParagraphSnapshots for affected paragraphs:
 * - All lines (from break_into_lines in core/src/lib.rs)
 * - Complete caret_map (from build_caret_map in core/src/caret_map.rs)
 * 
 * This function is kept for backward compatibility but should NOT be used
 * for normal incremental layout operations.
 * 
 * @param existingCache - Current cached paragraph snapshots
 * @param newSnapshots - Array of new ParagraphSnapshot from incremental layout
 * @param editDelta - Optional edit delta for adjusting grapheme indices of preserved lines
 * @returns New cache array with merged lines
 * @deprecated Use mergeSnapshotsByParagraphId instead
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function calculateNewSnapshotCache(
  existingCache: ParagraphSnapshot[],
  newSnapshots: ParagraphSnapshot[],
  editDelta?: EditDeltaInfo
): ParagraphSnapshot[] {
  // No new snapshots, return existing cache unchanged
  if (newSnapshots.length === 0) {
    return existingCache;
  }
  
  // No existing cache, just use new snapshots
  if (existingCache.length === 0) {
    return [...newSnapshots];
  }
  
  // For single-paragraph documents (most common case), merge lines within the snapshot
  // The new snapshot may only contain affected lines (incremental layout optimization)
  // We need to preserve unaffected lines from the existing cache
  
  const existingSnapshot = existingCache[0];
  const newSnapshot = newSnapshots[0];
  
  if (!existingSnapshot?.lines || !newSnapshot?.lines) {
    return [...newSnapshots];
  }
  
  // Calculate the grapheme shift: negative for delete, positive for insert
  // This is how much we need to adjust indices of lines AFTER the edit
  const graphemeShift = editDelta 
    ? (editDelta.insertLength - editDelta.deleteCount)
    : 0;
  
  // The edit affects graphemes from position to position + deleteCount (in OLD indices)
  // After the edit, this range becomes position to position + insertLength (in NEW indices)
  const editStartOld = editDelta?.position ?? 0;
  const editEndOld = editDelta ? (editDelta.position + editDelta.deleteCount) : 0;
  
  // If new snapshot has all lines (end_grapheme >= existing max adjusted for shift), use it as-is
  const existingMaxGrapheme = Math.max(...existingSnapshot.lines.map(l => l.end_grapheme_index));
  const newMaxGrapheme = Math.max(...newSnapshot.lines.map(l => l.end_grapheme_index));
  const newMinGrapheme = Math.min(...newSnapshot.lines.map(l => l.start_grapheme_index));
  
  // Adjust existingMaxGrapheme by the shift to compare properly
  const adjustedExistingMax = existingMaxGrapheme + graphemeShift;
  
  // If new snapshot covers the entire document (after accounting for shift), use it directly
  if (newMaxGrapheme >= adjustedExistingMax) {
    return [...newSnapshots];
  }
  
  // Merge: keep existing lines that don't overlap with the edit region
  // and add the new lines in their place
  const mergedLines: typeof existingSnapshot.lines = [];
  
  // Add existing lines that come BEFORE the edit region (no adjustment needed)
  // These lines end STRICTLY before the edit started, so they're unaffected
  // 🔬 FIX: Use < instead of <= to avoid including lines that END exactly at the edit position
  // When inserting at position 1 after "a" (line [0,1)), the line ends AT the edit position,
  // so it will be replaced by the new snapshot's line - don't add it here.
  // 🔬 FIX 2: Also check that the new snapshot doesn't already cover this range.
  // WASM may return all affected paragraphs, including paragraphs before the edit.
  for (const existingLine of existingSnapshot.lines) {
    if (existingLine.end_grapheme_index < editStartOld) {
      // Check if new snapshot already has a line covering this range
      const alreadyCovered = newSnapshot.lines.some(newLine => 
        newLine.start_grapheme_index <= existingLine.start_grapheme_index &&
        newLine.end_grapheme_index >= existingLine.end_grapheme_index
      );
      if (!alreadyCovered) {
        mergedLines.push(existingLine);
      }
    }
  }
  
  // Add all new lines (these replace the edit region with correct indices)
  mergedLines.push(...newSnapshot.lines);
  
  // Add existing lines that come AFTER the edit region, WITH ADJUSTED INDICES
  // These lines started after the edit ended, so their indices need to shift
  // 
  // 🔥 FIX (2026-01-12): Also filter out lines that would exceed the committed document length.
  // After DELETE operations, old lines may have grapheme indices that, even after shift adjustment,
  // refer to content that no longer exists.
  const committedLen = editDelta?.committedLength ?? Infinity;
  
  for (const existingLine of existingSnapshot.lines) {
    if (existingLine.start_grapheme_index >= editEndOld) {
      // Adjust grapheme indices by the shift amount
      const adjustedLine = {
        ...existingLine,
        start_grapheme_index: existingLine.start_grapheme_index + graphemeShift,
        end_grapheme_index: existingLine.end_grapheme_index + graphemeShift
      };
      
      // Only add if the adjusted line doesn't overlap with new lines
      // (this handles the case where the new snapshot already covers this range)
      // 🔥 FIX: Also check that the line doesn't exceed the committed document length
      if (adjustedLine.start_grapheme_index >= newMaxGrapheme && 
          adjustedLine.end_grapheme_index <= committedLen) {
        mergedLines.push(adjustedLine);
      } else if (adjustedLine.end_grapheme_index > committedLen) {
        console.warn(`🗑️ [MERGE] Filtering stale line [${adjustedLine.start_grapheme_index}-${adjustedLine.end_grapheme_index}) > committedLen=${committedLen}`);
      }
    }
  }
  
  // Sort by start_grapheme_index to maintain proper order
  mergedLines.sort((a, b) => a.start_grapheme_index - b.start_grapheme_index);
  
  // 🛡️ SAFETY NET: Deduplicate lines with same grapheme range
  // This catches any edge cases the merge logic above might miss
  const dedupedLines = deduplicateLinesByRange(mergedLines, 'calculateNewSnapshotCache');
  
  // Merge caret maps similarly with index adjustment
  const newCaretMinGrapheme = newSnapshot.caret_map?.[0]?.grapheme_index ?? newMinGrapheme;
  const newCaretMaxGrapheme = newSnapshot.caret_map?.[newSnapshot.caret_map.length - 1]?.grapheme_index ?? newMaxGrapheme;
  
  const mergedCaretMap: typeof existingSnapshot.caret_map = [];
  
  // Add existing caret entries that come BEFORE the edit (no adjustment)
  for (const entry of existingSnapshot.caret_map || []) {
    if (entry.grapheme_index < editStartOld) {
      mergedCaretMap.push(entry);
    }
  }
  
  // Add all new caret entries
  mergedCaretMap.push(...(newSnapshot.caret_map || []));
  
  // Add existing caret entries that come AFTER the edit, WITH ADJUSTED INDICES
  for (const entry of existingSnapshot.caret_map || []) {
    if (entry.grapheme_index >= editEndOld) {
      const adjustedEntry = {
        ...entry,
        grapheme_index: entry.grapheme_index + graphemeShift
      };
      // Only add if doesn't overlap with new entries
      if (adjustedEntry.grapheme_index >= newCaretMaxGrapheme) {
        mergedCaretMap.push(adjustedEntry);
      }
    }
  }
  
  // Sort by grapheme_index
  mergedCaretMap.sort((a, b) => a.grapheme_index - b.grapheme_index);
  
  // Calculate proper total dimensions from deduped lines
  const totalHeight = dedupedLines.length > 0 
    ? dedupedLines.reduce((sum, line) => sum + (line.height_fp || 0), 0)
    : newSnapshot.total_height_fp;
  const totalWidth = dedupedLines.length > 0
    ? Math.max(...dedupedLines.map(line => line.width_fp || 0))
    : newSnapshot.total_width_fp;
  
  // Create merged snapshot
  const mergedSnapshot: ParagraphSnapshot = {
    ...newSnapshot,
    lines: dedupedLines,
    caret_map: mergedCaretMap,
    total_height_fp: totalHeight,
    total_width_fp: totalWidth
  };
  
  return [mergedSnapshot];
}

/**
 * Shift a snapshot's grapheme indices AND Y positions by delta amounts.
 * Creates a new snapshot with adjusted line and caret_map indices and Y positions.
 * 
 * @param snapshot - The snapshot to shift
 * @param graphemeShift - Delta for grapheme indices (positive = insert, negative = delete)
 * @param yShift - Delta for Y positions in fp units (positive = move down, negative = move up)
 */
function shiftSnapshotIndices(
  snapshot: ParagraphSnapshot, 
  graphemeShift: number,
  yShift: number = 0
): ParagraphSnapshot {
  // Shift all line indices AND Y positions if yShift is provided
  const shiftedLines = snapshot.lines?.map(line => ({
    ...line,
    start_grapheme_index: line.start_grapheme_index + graphemeShift,
    end_grapheme_index: line.end_grapheme_index + graphemeShift,
    // 🔥 FIX: Shift Y positions for MS Word-like behavior
    // When content above changes height, subsequent paragraphs should move up/down
    baseline_y_fp: (line.baseline_y_fp ?? 0) + yShift
  })) ?? [];
  
  // Shift caret_map entries (grapheme indices AND Y positions)
  const shiftedCaretMap = snapshot.caret_map?.map(entry => ({
    ...entry,
    grapheme_index: entry.grapheme_index + graphemeShift,
    // 🔥 FIX: Shift caret Y positions too
    y_fp: (entry.y_fp ?? 0) + yShift
  })) ?? [];
  
  // Shift snapshot-level grapheme fields if present
  const shiftedSnapshot: ParagraphSnapshot = {
    ...snapshot,
    lines: shiftedLines,
    caret_map: shiftedCaretMap,
  };
  
  // Shift start_grapheme_index and end_grapheme_index if they exist on the snapshot
  if (typeof (snapshot as any).start_grapheme_index === 'number') {
    (shiftedSnapshot as any).start_grapheme_index = (snapshot as any).start_grapheme_index + graphemeShift;
  }
  if (typeof (snapshot as any).end_grapheme_index === 'number') {
    (shiftedSnapshot as any).end_grapheme_index = (snapshot as any).end_grapheme_index + graphemeShift;
  }
  
  return shiftedSnapshot;
}

/**
 * Merge new snapshots into existing cache by paragraph ID
 * 
 * PRINCIPLE: WASM is the single source of truth.
 * 
 * Algorithm:
 * 1. Take WASM snapshots VERBATIM (no modification)
 * 2. Preserve cached snapshots that are UNAFFECTED (with shifted indices)
 * 3. Sort by document order
 * 
 * @param existingCache - Current cached paragraph snapshots
 * @param newSnapshots - Array of new ParagraphSnapshot from WASM (source of truth)
 * @param editDelta - Optional edit delta (used for grapheme shifting)
 * @param affectedParagraphIds - Paragraph IDs that WASM handled
 * @param deletedParagraphIds - Paragraph IDs that WASM deleted/merged
 * @returns New cache array with merged snapshots, sorted by document order
 */
export function mergeSnapshotsByParagraphId(
  existingCache: ParagraphSnapshot[],
  newSnapshots: ParagraphSnapshot[],
  editDelta?: EditDeltaInfo,
  affectedParagraphIds?: string[],
  deletedParagraphIds?: string[],
  allParagraphIds?: string[]
): ParagraphSnapshot[] {
  // ============================================================================
  // 🔥 CACHE EVICTION STRATEGY (2026-01-13)
  // ============================================================================
  // Instead of complex merge logic that tries to preserve+filter cached data,
  // we use a simple eviction-first approach:
  //
  // 1. EVICT: Remove ALL cached snapshots for paragraphs that WASM touched
  // 2. INSERT: Add WASM snapshots verbatim (source of truth)
  // 3. PRESERVE: Keep cached snapshots for truly unaffected paragraphs
  //
  // This eliminates stale data bugs because we never try to merge stale+fresh.
  // ============================================================================
  
  const shift = editDelta ? (editDelta.insertLength - editDelta.deleteCount) : 0;
  const editBoundary = editDelta?.position ?? 0;
  const committedLen = editDelta?.committedLength ?? Infinity;
  
  // Build eviction sets - these paragraph IDs should NOT be preserved from cache
  const evictSet = new Set<string>();
  
  // Evict: Paragraphs WASM returned snapshots for
  for (const s of newSnapshots) {
    const key = getParaKey(s);
    if (key) evictSet.add(key);
  }
  
  // Evict: Paragraphs WASM marked as affected
  for (const id of (affectedParagraphIds ?? [])) {
    evictSet.add(id);
  }
  
  // Evict: Paragraphs WASM deleted/merged
  for (const id of (deletedParagraphIds ?? [])) {
    evictSet.add(id);
  }
  
  // Valid paragraph IDs (if WASM provided them)
  const validParaIds = allParagraphIds ? new Set(allParagraphIds) : null;
  
  // 🔬 DEBUG: Log allParagraphIds to see if it's populated
  WasmLogger.snapshot.evict('allParagraphIds check', {
    allParagraphIdsReceived: !!allParagraphIds,
    allParagraphIdsCount: allParagraphIds?.length ?? 0,
    allParagraphIds: allParagraphIds?.map(id => id?.substring(0, 8)) ?? 'undefined',
    cachedParaIds: existingCache.map(s => getParaKey(s)?.substring(0, 8))
  });

  // 🔬 NEW (2026-01-19): Log paragraph IDs at different stages to diagnose duplicates
  WasmLogger.delta.allParagraphIds({
    allIds: allParagraphIds ?? [],
    affectedIds: affectedParagraphIds ?? [],
    snapshotIds: newSnapshots.map(s => getParaKey(s) ?? s.snapshot_id ?? 'unknown'),
    phase: 'before_layout'
  });
  
  // 🔬 DEBUG: Log EVERY WASM snapshot with its lines
  WasmLogger.snapshot.evict(`=== WASM SNAPSHOTS (${newSnapshots.length}) ===`, {});
  for (const snap of newSnapshots) {
    const paraKey = getParaKey(snap)?.substring(0, 8) ?? 'unknown';
    const lines = snap.lines ?? [];
    WasmLogger.snapshot.evict(`WASM para ${paraKey}: ${lines.length} lines`, {
      lineRanges: lines.map(l => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', ')
    });
  }
  
  // 🔬 DEBUG: Log EVERY CACHED snapshot with its lines BEFORE processing
  WasmLogger.snapshot.evict(`=== CACHED SNAPSHOTS (${existingCache.length}) ===`, {});
  for (const snap of existingCache) {
    const paraKey = getParaKey(snap)?.substring(0, 8) ?? 'unknown';
    const lines = snap.lines ?? [];
    WasmLogger.snapshot.evict(`CACHED para ${paraKey}: ${lines.length} lines`, {
      lineRanges: lines.map(l => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', ')
    });
  }
  
  WasmLogger.snapshot.evict('Cache eviction strategy', {
    wasmSnapshotsCount: newSnapshots.length,
    cachedSnapshotsCount: existingCache.length,
    evictCount: evictSet.size,
    validParaIdsCount: validParaIds?.size ?? 'N/A',
    shift,
    editBoundary,
    committedLen
  });
  
  // 🔬 DEBUG (2026-01-13): Dump ALL WASM line ranges to diagnose overlaps
  // This will show if WASM itself returns overlapping lines from different paragraphs
  const allWasmLines: Array<{ paraId: string; snapshotId: string; lineIdx: number; start: number; end: number }> = [];
  for (const snapshot of newSnapshots) {
    const paraId = getParaKey(snapshot) ?? 'unknown';
    const snapshotId = snapshot.snapshot_id ?? 'unknown';
    for (let i = 0; i < (snapshot.lines?.length ?? 0); i++) {
      const line = snapshot.lines![i];
      allWasmLines.push({
        paraId: paraId.substring(0, 8),
        snapshotId: snapshotId.substring(0, 8),
        lineIdx: i,
        start: line.start_grapheme_index,
        end: line.end_grapheme_index
      });
    }
  }
  // Sort by start position and check for overlaps
  allWasmLines.sort((a, b) => a.start - b.start);
  const wasmOverlaps: string[] = [];
  for (let i = 1; i < allWasmLines.length; i++) {
    const prev = allWasmLines[i - 1];
    const curr = allWasmLines[i];
    // Overlap if prev.end > curr.start (prev extends into curr's range)
    if (prev.end > curr.start) {
      wasmOverlaps.push(`${prev.paraId}[${prev.start}-${prev.end}) overlaps ${curr.paraId}[${curr.start}-${curr.end})`);

      // 🔬 NEW (2026-01-19): Use new detailed paragraph overlap logging
      // Determine overlap type
      const isExactMatch = prev.start === curr.start && prev.end === curr.end;
      const prevContainsCurr = prev.start <= curr.start && prev.end >= curr.end;
      const currContainsPrev = curr.start <= prev.start && curr.end >= prev.end;
      const overlapType = isExactMatch ? 'exact_match' as const :
                          (prevContainsCurr || currContainsPrev) ? 'containment' as const : 'partial_overlap' as const;

      // Find the full snapshot info for detailed logging
      const prevSnapshot = newSnapshots.find(s => (getParaKey(s) ?? 'unknown').substring(0, 8) === prev.paraId);
      const currSnapshot = newSnapshots.find(s => (getParaKey(s) ?? 'unknown').substring(0, 8) === curr.paraId);

      WasmLogger.delta.paragraphRangeOverlap({
        paragraph1: {
          id: prev.paraId,
          snapshotId: prev.snapshotId,
          graphemeStart: prev.start,
          graphemeEnd: prev.end,
          linesCount: prevSnapshot?.lines?.length ?? 0
        },
        paragraph2: {
          id: curr.paraId,
          snapshotId: curr.snapshotId,
          graphemeStart: curr.start,
          graphemeEnd: curr.end,
          linesCount: currSnapshot?.lines?.length ?? 0
        },
        overlapType,
        context: `WASM returned overlapping paragraphs (affectedIds: ${affectedParagraphIds?.map(id => id.substring(0, 8)).join(', ') ?? 'none'})`
      });
    }
  }
  if (wasmOverlaps.length > 0) {
    // Log summary with all paragraph info for correlation
    console.error(`🔴 [SnapshotCacheUpdater] WASM returned ${wasmOverlaps.length} OVERLAPPING lines from DIFFERENT paragraphs:`, {
      overlaps: wasmOverlaps,
      allParagraphIds: allParagraphIds?.map(id => id?.substring(0, 8)),
      affectedParagraphIds: affectedParagraphIds?.map(id => id?.substring(0, 8)),
      deletedParagraphIds: deletedParagraphIds?.map(id => id?.substring(0, 8)),
      newSnapshotParaIds: newSnapshots.map(s => ({
        paraId: getParaKey(s)?.substring(0, 8),
        snapshotId: s.snapshot_id?.substring(0, 8),
        linesCount: s.lines?.length ?? 0,
        graphemeRange: s.lines?.length ? `[${s.lines[0].start_grapheme_index}-${s.lines[s.lines.length - 1].end_grapheme_index})` : 'no lines'
      }))
    });
  }
  
  // �🔥 FIX (2026-01-13): Filter WASM snapshots for stale lines too!
  // WASM snapshots may have stale lines due to sync issues or race conditions.
  // Filter any lines with grapheme indices beyond committedLen BEFORE adding to result.
  const sanitizedWasmSnapshots: ParagraphSnapshot[] = [];
  for (const snapshot of newSnapshots) {
    if (snapshot.lines && snapshot.lines.length > 0) {
      const filteredLines = snapshot.lines.filter(line => line.end_grapheme_index <= committedLen);
      if (filteredLines.length < snapshot.lines.length) {
        WasmLogger.cacheWarning(`Filtered ${snapshot.lines.length - filteredLines.length} stale lines from WASM snapshot (end > ${committedLen})`, {});
      }
      sanitizedWasmSnapshots.push({
        ...snapshot,
        lines: filteredLines
      });
    } else {
      sanitizedWasmSnapshots.push(snapshot);
    }
  }
  
  // 🔥 FIX (2026-01-13): Build a set of WASM line ranges to detect cached lines that overlap
  // When WASM returns a line like [27-73), any cached lines that intersect that range
  // must be evicted because they're from an old document structure.
  const wasmLineRanges: Array<{ start: number; end: number }> = [];
  for (const snapshot of sanitizedWasmSnapshots) {
    for (const line of snapshot.lines ?? []) {
      wasmLineRanges.push({ start: line.start_grapheme_index, end: line.end_grapheme_index });
    }
  }
  
  // Helper: Check if a line INTERSECTS with any WASM line's range
  // Two ranges [a,b) and [c,d) intersect if: a < d AND c < b (neither ends before the other starts)
  const lineOverlapsWithWasm = (lineStart: number, lineEnd: number): boolean => {
    for (const wasmRange of wasmLineRanges) {
      // Check for ANY intersection, not just containment
      // [34-98) intersects [30-94) because 34 < 94 AND 30 < 98
      // Also handles: complete containment, partial overlap from either side
      // Exclude exact matches (same line from WASM = OK to keep)
      const isExactMatch = lineStart === wasmRange.start && lineEnd === wasmRange.end;
      const hasIntersection = lineStart < wasmRange.end && wasmRange.start < lineEnd;
      
      if (hasIntersection && !isExactMatch) {
        return true;
      }
    }
    return false;
  };
  
  // Step 1: Start with SANITIZED WASM snapshots (source of truth)
  const result: ParagraphSnapshot[] = [...sanitizedWasmSnapshots];
  const resultKeys = new Set(sanitizedWasmSnapshots.map(s => getParaKey(s)).filter(Boolean));
  
  // Step 2: Preserve UNAFFECTED cached snapshots
  let preserved = 0;
  let evicted = 0;
  
  for (const cached of existingCache) {
    const key = getParaKey(cached);
    if (!key) {
      WasmLogger.cacheWarning('Skipping cached snapshot with no key', {});
      continue;
    }
    
    const cachedLines = cached.lines ?? [];
    const cachedLineRanges = cachedLines.map(l => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', ');
    WasmLogger.snapshot.evict(`Processing cached ${key.substring(0, 8)}`, { lines: cachedLineRanges });
    
    // EVICT if this paragraph was touched by WASM
    if (evictSet.has(key)) {
      evicted++;
      WasmLogger.snapshot.evict(`Evicted (in evictSet): ${key.substring(0, 8)} - WASM touched this para`, {});
      continue;
    }
    
    // EVICT if paragraph no longer exists in document
    if (validParaIds && !validParaIds.has(key)) {
      evicted++;
      WasmLogger.snapshot.evict(`Evicted (orphan): ${key.substring(0, 8)} - not in validParaIds`, {});
      continue;
    }
    
    // EVICT if already in result (duplicate)
    if (resultKeys.has(key)) {
      evicted++;
      WasmLogger.snapshot.evict(`Evicted (dup): ${key.substring(0, 8)} - already in result`, {});
      continue;
    }
    
    // 🔥 FIX (2026-01-13): Check staleness AFTER shift, not before!
    // The shift can push indices beyond committedLen, creating stale lines.
    // Example: lines [45-50) with shift +10 become [55-60), which may exceed committedLen=52
    const startGrapheme = cached.lines?.[0]?.start_grapheme_index ?? 0;
    const needsShift = startGrapheme >= editBoundary && shift !== 0;
    
    // Calculate what the indices will be AFTER shift
    const lines = cached.lines ?? [];
    let maxGraphemePreShift = 0;
    let maxGraphemePostShift = 0;
    if (lines.length > 0) {
      maxGraphemePreShift = Math.max(...lines.map(l => l.end_grapheme_index));
      maxGraphemePostShift = needsShift ? maxGraphemePreShift + shift : maxGraphemePreShift;
      
      // 🔬 DEBUG: Always log staleness check for diagnosis
      WasmLogger.snapshot.evict(`Staleness check for ${key.substring(0, 8)}`, {
        startGrapheme, editBoundary, needsShift, maxPre: maxGraphemePreShift, maxPost: maxGraphemePostShift, committedLen, isStale: maxGraphemePostShift > committedLen
      });
      
      if (maxGraphemePostShift > committedLen) {
        evicted++;
        WasmLogger.cacheWarning(`Evicted (stale post-shift): ${key.substring(0, 8)} maxGrapheme=${maxGraphemePreShift}→${maxGraphemePostShift} > committedLen=${committedLen}`, {});
        continue;
      }
      
      // 🔥 FIX (2026-01-13): LOG if ANY line overlaps with WASM lines (diagnostic only)
      // NOTE: We do NOT evict here because WASM didn't return fresh data for this paragraph.
      // Evicting would cause data loss. The render-time filter in EdgeDocsCanvas will handle overlaps.
      // This log helps diagnose if WASM should have returned this paragraph but didn't.
      for (const line of lines) {
        const lineStartPost = needsShift ? line.start_grapheme_index + shift : line.start_grapheme_index;
        const lineEndPost = needsShift ? line.end_grapheme_index + shift : line.end_grapheme_index;
        if (lineOverlapsWithWasm(lineStartPost, lineEndPost)) {
          WasmLogger.cacheWarning(`Line overlap detected (NOT evicting): ${key.substring(0, 8)} line [${lineStartPost}-${lineEndPost}) overlaps with WASM`, {});
          break; // Log once per paragraph
        }
      }
    }
    
    // PRESERVE: Shift indices if needed and keep
    const preserved_snapshot = needsShift
      ? shiftSnapshotIndices(cached, shift)
      : cached;
    
    // 🔬 DEBUG: Log what we're preserving with staleness check details
    const preservedMaxGrapheme = preserved_snapshot.lines && preserved_snapshot.lines.length > 0
      ? Math.max(...preserved_snapshot.lines.map(l => l.end_grapheme_index))
      : 0;
    if (preservedMaxGrapheme > committedLen) {
      WasmLogger.error('merge_evict', new Error(`BUG: Preserving stale snapshot! ${key.substring(0, 8)} maxGrapheme=${preservedMaxGrapheme} > committedLen=${committedLen}`), { needsShift, preShiftMax: maxGraphemePreShift, postShiftMax: maxGraphemePostShift });
    }
    
    const preservedLineRanges = (preserved_snapshot.lines ?? []).map(l => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', ');
    WasmLogger.snapshot.preserve(`Preserved: ${key.substring(0, 8)} (shifted=${needsShift})`, { lines: preservedLineRanges });
    result.push(preserved_snapshot);
    resultKeys.add(key);
    preserved++;
  }
  
  WasmLogger.snapshot.result(`Result: ${result.length} snapshots (${newSnapshots.length} WASM + ${preserved} preserved, ${evicted} evicted)`, {});
  
  // 🔬 DEBUG: Log final result with all lines
  WasmLogger.snapshot.result(`=== FINAL RESULT (${result.length}) ===`, {});
  for (const snap of result) {
    const paraKey = getParaKey(snap)?.substring(0, 8) ?? 'unknown';
    const lines = snap.lines ?? [];
    WasmLogger.snapshot.result(`RESULT para ${paraKey}: ${lines.length} lines`, {
      lineRanges: lines.map(l => `[${l.start_grapheme_index}-${l.end_grapheme_index})`).join(', ')
    });
  }

  // 🔬 NEW (2026-01-19): Log paragraph IDs after merge to track ID propagation
  WasmLogger.delta.allParagraphIds({
    allIds: result.map(s => getParaKey(s) ?? s.snapshot_id ?? 'unknown'),
    affectedIds: affectedParagraphIds ?? [],
    snapshotIds: result.map(s => s.snapshot_id ?? 'unknown'),
    phase: 'after_merge'
  });

  // Step 3: Sort by document order
  result.sort((a, b) => {
    const aStart = a.lines?.[0]?.start_grapheme_index ?? 0;
    const bStart = b.lines?.[0]?.start_grapheme_index ?? 0;
    return aStart - bStart;
  });
  
  // 🔥 FIX (2026-01-20): Recalculate Y positions for ALL snapshots
  // When we mix WASM snapshots (fresh Y positions) with preserved cached snapshots (old Y positions),
  // the Y positions become inconsistent. The fix is to recalculate cumulative Y positions
  // for ALL snapshots based on their heights in document order.
  //
  // This ensures paragraph 0's lines start at the content area top (origin_y_fp from WASM),
  // and subsequent paragraphs stack correctly below.
  //
  // Note: We use the FIRST paragraph's current Y position as the origin,
  // since that's where the content area starts (includes top margin).
  
  if (result.length > 0) {
    // Find the origin Y position from the FIRST paragraph in document order
    // This is the top of the content area (includes margin)
    // The first paragraph's first line Y is the origin - it should NOT be shifted.
    let originYFp = 0;
    const firstSnapshotWithLines = result.find(s => s.lines && s.lines.length > 0);
    if (firstSnapshotWithLines && firstSnapshotWithLines.lines && firstSnapshotWithLines.lines.length > 0) {
      // Use the first paragraph's current Y as the origin
      // This works for both cached and WASM snapshots
      originYFp = firstSnapshotWithLines.lines[0].baseline_y_fp;
      
      // 🔬 DIAGNOSTIC: Log origin detection
      const firstKey = getParaKey(firstSnapshotWithLines)?.substring(0, 8) ?? 'unknown';
      WasmLogger.snapshot.result(`Y_RECALC_ORIGIN: using para ${firstKey} with Y=${originYFp}fp as origin`, {
        paraKey: firstKey,
        originYFp
      });
    }
    
    // Recalculate Y positions for all snapshots EXCEPT the first one
    // The first paragraph keeps its Y position (it defines the origin)
    // Subsequent paragraphs get recalculated based on cumulative heights
    let cumulativeYOffset = 0;
    const recalculatedResult: ParagraphSnapshot[] = [];
    
    for (let i = 0; i < result.length; i++) {
      const snapshot = result[i];
      const key = getParaKey(snapshot)?.substring(0, 8) ?? 'unknown';
      const lines = snapshot.lines ?? [];
      
      if (lines.length === 0) {
        recalculatedResult.push(snapshot);
        continue;
      }
      
      // Get the current first line Y position
      const currentFirstLineY = lines[0].baseline_y_fp;
      
      // Calculate where the first line SHOULD be (origin + cumulative offset)
      const targetFirstLineY = originYFp + cumulativeYOffset;
      
      // Calculate the Y shift needed
      const yShift = targetFirstLineY - currentFirstLineY;
      
      // 🔬 DIAGNOSTIC: Log Y recalculation
      WasmLogger.snapshot.result(`Y_RECALC: para ${key} currentY=${currentFirstLineY}fp targetY=${targetFirstLineY}fp shift=${yShift}fp cumulative=${cumulativeYOffset}fp`, {
        paraKey: key,
        currentFirstLineY,
        targetFirstLineY,
        yShift,
        cumulativeYOffset,
        totalHeightFp: snapshot.total_height_fp
      });
      
      if (yShift !== 0) {
        // Apply Y shift to all lines
        const shiftedLines = lines.map(line => ({
          ...line,
          baseline_y_fp: line.baseline_y_fp + yShift
        }));
        
        // Apply Y shift to caret_map
        const shiftedCaretMap = (snapshot.caret_map ?? []).map(entry => ({
          ...entry,
          y_fp: (entry.y_fp ?? 0) + yShift
        }));
        
        recalculatedResult.push({
          ...snapshot,
          lines: shiftedLines,
          caret_map: shiftedCaretMap
        });
      } else {
        recalculatedResult.push(snapshot);
      }
      
      // Add this paragraph's height to the cumulative offset
      cumulativeYOffset += snapshot.total_height_fp ?? 0;
    }
    
    return recalculatedResult;
  }
  
  return result;
}
  
